/**
 * Deactivate, reactivate, delete — the client side of 20260929000002.
 *
 * Drew, 2026-09-29: nobody hard-deletes a person on click. DEACTIVATE takes
 * them off every screen and every login that second and keeps their clients,
 * hidden, under their real name; DELETE (platform staff only, for test junk)
 * wipes now; SUSPEND stays a billing lock. A person who deactivated
 * themselves has SELF_REACTIVATE_DAYS to come back by signing in; platform
 * staff can bring anyone back from the Admin Panel until the nightly purge
 * removes what has been dark for ARCHIVE_YEARS. Every function here is a
 * SECURITY DEFINER RPC that carries its own gate; a refusal is thrown, never
 * swallowed.
 */
import { supabase } from '@/lib/safeSupabase';

/** Sign back in within this many days of deactivating yourself and you're back.
 *  Pinned to reactivate_self()'s `interval '30 days'` by a test. */
export const SELF_REACTIVATE_DAYS = 30;
/** Deactivated people and orgs are wiped by the nightly job after this long.
 *  Pinned to purge_deactivated()'s `interval '7 years'` by a test. */
export const ARCHIVE_YEARS = 7;

/** The "let me choose" plan: people and sub-orgs placed OUTSIDE the branch
 *  before it goes dark or is wiped. Whatever is not placed goes with it. */
export interface BranchPlan {
  people: { user_id: string; org_id: string; role: 'agent' | 'org_admin' }[];
  orgs: { org_id: string; parent_id: string }[];
}

type Rpc = (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: Error | null }>;
const rpc = async <T,>(fn: string, args?: Record<string, unknown>): Promise<T> => {
  const { data, error } = await (supabase.rpc as unknown as Rpc)(fn, args);
  if (error) throw error;
  return data as T;
};

/** A person (never an owner: their org is deactivated instead). Platform staff
 *  anywhere, else someone who runs the person's org. */
export const deactivateMember = (userId: string) => rpc<void>('deactivate_member', { p_user: userId });

/** An org with everything under it. Resolves to how many people went dark. */
export async function deactivateOrg(orgId: string, plan?: BranchPlan | null): Promise<number> {
  const r = await rpc<{ people?: number } | null>('deactivate_org', { p_org: orgId, p_plan: plan ?? null });
  return Number(r?.people ?? 0);
}

/** Your own account, from Settings. An owner takes their org with them. */
export const deactivateSelf = () => rpc<void>('deactivate_self');

/** Signing back in within SELF_REACTIVATE_DAYS. Resolves false when the window
 *  has passed or an administrator did the deactivating. */
export const reactivateSelf = () => rpc<boolean>('reactivate_self').then(v => v === true);

export const reactivateMember = (userId: string) => rpc<void>('reactivate_member', { p_user: userId });

export async function reactivateOrg(orgId: string): Promise<number> {
  const r = await rpc<{ people?: number } | null>('reactivate_org', { p_org: orgId });
  return Number(r?.people ?? 0);
}

/** Wipe an org, its people, their logins and their clients now. Platform staff only. */
export async function purgeBranch(orgId: string, plan?: BranchPlan | null): Promise<{ orgs: number; people: number }> {
  const r = await rpc<{ orgs?: number; people?: number } | null>('purge_branch', { p_org: orgId, p_plan: plan ?? null });
  return { orgs: Number(r?.orgs ?? 0), people: Number(r?.people ?? 0) };
}

/** When the nightly job will wipe something deactivated at `at`. */
export function purgeDate(at: string | Date): Date {
  const d = new Date(at);
  d.setFullYear(d.getFullYear() + ARCHIVE_YEARS);
  return d;
}
